import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Download, Command } from 'lucide-react';

// Import components
import SearchBar from './SearchBar';
import FrameworkSelector from './explorer/FrameworkSelector';
import DomainSelector from './explorer/DomainSelector';
import SearchResults from './explorer/SearchResults';
import ControlList from './explorer/ControlList';
import TTPList from './explorer/TTPList';
import ControlDetails from './explorer/ControlDetails';
import TTPDetails from './explorer/TTPDetails';
import FrameworkComparison from './explorer/FrameworkComparison';
import CoverageView from './explorer/CoverageView';
import CommandPalette from './explorer/CommandPalette';

// Import hooks and utilities
import { useFrameworkControls } from '../hooks/useFrameworkControls';
import { useTTPDetails } from '../hooks/useTTPDetails';
import highlightSearchText from '../utils/textHighlighter';
import { findMappedControls } from '../utils/controlMapper';
import { getFramework, getControlFrameworks } from '../data/frameworks.registry';
import { getMitigatingControls, getTechniquesCoveredBy, getEntitiesByDomain } from '../lib/selectors';
import { normaliseAttackId } from '../lib/attack-ids';
import { domains as DOMAINS } from '../data/domains';
import { readDeepLink, writeDeepLink } from '../lib/deep-link';
import { buildReport, downloadReport } from '../lib/export';

// The control frameworks the picker offers (registry-driven, populated only).
const CONTROL_FRAMEWORKS = getControlFrameworks({ populatedOnly: true });
const CONTROL_FRAMEWORK_IDS = new Set(CONTROL_FRAMEWORKS.map((f) => f.id));
const DOMAIN_IDS = new Set(DOMAINS.map((d) => d.id));

// Initial state from the URL hash, validated against the registry/domains so a
// stale or hand-edited link degrades to defaults rather than breaking.
const INITIAL = readDeepLink();
const INITIAL_FRAMEWORK = getFramework(INITIAL.frameworkId)?.entityKind === 'control'
  ? INITIAL.frameworkId
  : 'nist-800-53-r5';
const INITIAL_DOMAIN = INITIAL.domain && DOMAIN_IDS.has(INITIAL.domain) ? INITIAL.domain : 'NETWORK';

// The "other" control framework to compare against — registry-driven, no name
// literals (ADR-2). With NIST + ISO populated this is simply the other one.
const getAlternateControlFrameworkId = (currentId) => {
  const other = CONTROL_FRAMEWORKS.find((f) => f.id !== currentId);
  return other ? other.id : currentId;
};

const ZeroTrustExplorer = () => {
  // State management (initialised from the URL hash for deep-linking).
  const [selectedFrameworkId, setSelectedFrameworkId] = useState(INITIAL_FRAMEWORK);
  const [selectedDomain, setSelectedDomain] = useState(INITIAL_DOMAIN);
  const [selectedControl, setSelectedControl] = useState(INITIAL.control || '');
  const [selectedTTP, setSelectedTTP] = useState(INITIAL.ttp || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('explore'); // 'explore' or 'coverage'
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState({
    primaryControl: '',
    primaryFramework: '',
    primaryDetails: null,
    secondaryControl: '',
    secondaryFramework: '',
    secondaryDetails: null
  });

  // Registry-derived display label (no name-string branching in render).
  const selectedFramework = getFramework(selectedFrameworkId)?.label ?? '';

  // Custom hooks for primary framework
  const { getCurrentFrameworkControls, getControlDetails } = useFrameworkControls(
    selectedFrameworkId,
    selectedDomain
  );

  // Custom hooks for secondary framework (to avoid rules of hooks violation)
  const secondaryFrameworkId = getAlternateControlFrameworkId(selectedFrameworkId);
  const secondaryFramework = getFramework(secondaryFrameworkId)?.label ?? '';
  const { getControlDetails: getSecondaryControlDetails } = useFrameworkControls(
    secondaryFrameworkId,
    selectedDomain
  );

  const { getCurrentDomainTTPs, getTTPDetails } = useTTPDetails(selectedDomain);
  
  // Handle comparison button click
  const handleCompareControls = useCallback(() => {
    if (!selectedControl) return;
    
    const primaryFramework = selectedFramework;
    const primaryControl = selectedControl;
    const primaryDetails = getControlDetails(primaryControl);

    // Find the equivalent control in the alternate framework via the crosswalk.
    const mappedControls = findMappedControls(primaryControl, primaryFramework);

    // Use the first mapped control if available
    let secondaryControl = mappedControls.length > 0 ? mappedControls[0] : '';
    let secondaryDetails = null;

    if (secondaryControl) {
      secondaryDetails = getSecondaryControlDetails(secondaryControl);
    }

    // Set comparison data and show comparison view
    setComparisonData({
      primaryControl,
      primaryFramework,
      primaryDetails,
      secondaryControl,
      secondaryFramework,
      secondaryDetails
    });

    setShowComparison(true);
  }, [
    selectedControl,
    selectedFramework,
    secondaryFramework,
    getControlDetails,
    getSecondaryControlDetails
  ]);

  // Event handlers
  const handleFrameworkChange = (frameworkId) => {
    setSelectedFrameworkId(frameworkId);
    setSelectedControl('');
    setSelectedTTP('');
    setShowComparison(false);
  };

  const handleDomainChange = (domain) => {
    setSelectedDomain(domain);
    setSelectedControl('');
    setSelectedTTP('');
    setShowComparison(false);
  };

  const handleControlSelect = (control) => {
    setSelectedControl(control);
    setSelectedTTP(''); // Clear TTP selection when a control is selected
    setShowComparison(false);
  };

  const handleTTPSelect = (ttp) => {
    setSelectedTTP(ttp);
    setSelectedControl(''); // Clear control selection when a TTP is selected
    setShowComparison(false);
  };
  
  // Handle closing the comparison view
  const handleCloseComparison = () => {
    setShowComparison(false);
  };

  // Filter controls based on search query
  const filteredControls = useMemo(() => {
    if (!searchQuery.trim()) {
      return getCurrentFrameworkControls();
    }

    const query = searchQuery.toLowerCase();
    const controls = getCurrentFrameworkControls();
    const filteredResults = {};

    // Search through control categories
    Object.entries(controls).forEach(([category, controlList]) => {
      const matchedControls = (controlList?.controls || []).filter(controlId => {
        const controlDetails = getControlDetails(controlId);
        
        // Check if control ID matches
        if (controlId.toLowerCase().includes(query)) return true;
        
        // Check if control name or description matches
        if (controlDetails) {
          if (controlDetails.name && controlDetails.name.toLowerCase().includes(query)) return true;
          if (controlDetails.description && controlDetails.description.toLowerCase().includes(query)) return true;
          
          // Check in threat events if they exist
          if (controlDetails.threat_events) {
            for (const event of controlDetails.threat_events) {
              if (typeof event === 'string' && event.toLowerCase().includes(query)) return true;
              if (typeof event === 'object' && event.name && event.name.toLowerCase().includes(query)) return true;
            }
          }
        }
        
        return false;
      });

      if (matchedControls.length > 0) {
        filteredResults[category] = { controls: matchedControls };
      }
    });

    return filteredResults;
  }, [searchQuery, getCurrentFrameworkControls, getControlDetails]);

  // Filter TTPs based on search query
  const filteredTTPs = useMemo(() => {
    if (!searchQuery.trim()) {
      return getCurrentDomainTTPs();
    }

    const query = searchQuery.toLowerCase();
    return getCurrentDomainTTPs().filter(ttpId => {
      const ttpDetails = getTTPDetails(ttpId);
      
      // Check if TTP ID matches
      if (ttpId.toLowerCase().includes(query)) return true;
      
      // Check if TTP details match
      if (ttpDetails) {
        if (ttpDetails.technique_name && ttpDetails.technique_name.toLowerCase().includes(query)) return true;
        if (ttpDetails.tactic && ttpDetails.tactic.toLowerCase().includes(query)) return true;
        if (ttpDetails.technique_desc && ttpDetails.technique_desc.toLowerCase().includes(query)) return true;
        
        // Check in mitigations if they exist
        if (ttpDetails.mitigations) {
          for (const mitigation of ttpDetails.mitigations) {
            if (mitigation.toLowerCase().includes(query)) return true;
          }
        }
      }
      
      return false;
    });
  }, [searchQuery, getCurrentDomainTTPs, getTTPDetails]);

  // Calculate total control count for search results
  const totalControlCount = useMemo(() => {
    return Object.values(filteredControls).reduce(
      (count, category) => count + (category.controls?.length || 0), 
      0
    );
  }, [filteredControls]);

  // Get current control and TTP details
  const controlDetails = useMemo(() => {
    return selectedControl ? getControlDetails(selectedControl) : null;
  }, [selectedControl, getControlDetails]);

  const ttpDetails = useMemo(() => {
    return selectedTTP ? getTTPDetails(selectedTTP) : null;
  }, [selectedTTP, getTTPDetails]);

  // ⌘K / Ctrl+K toggles the command palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Export the current view (coverage grid, or the selected entity) as a
  // provenance-carrying report suitable for a NIST 800-30 risk assessment.
  const handleExport = useCallback((type) => {
    const domainLabel = DOMAINS.find((d) => d.id === selectedDomain)?.label || selectedDomain;
    const report = buildReport({
      viewMode,
      domainId: selectedDomain,
      domainLabel,
      selectedControl,
      selectedTTP,
    });
    downloadReport(report.filename, type === 'json' ? report.json : report.markdown, type);
  }, [viewMode, selectedDomain, selectedControl, selectedTTP]);

  // Navigate to an entity chosen from the command palette.
  const handlePaletteSelect = useCallback((entity) => {
    setPaletteOpen(false);
    setViewMode('explore');
    if (entity.kind === 'control') {
      if (CONTROL_FRAMEWORK_IDS.has(entity.frameworkId)) setSelectedFrameworkId(entity.frameworkId);
      setSelectedTTP('');
      setShowComparison(false);
      setSelectedControl(entity.id);
    } else if (entity.kind === 'offensive-technique') {
      if (entity.domains?.length && !entity.domains.includes(selectedDomain)) {
        setSelectedDomain(entity.domains[0]);
      }
      setSelectedControl('');
      setShowComparison(false);
      setSelectedTTP(entity.id);
    }
  }, [selectedDomain]);

  // Keep the URL hash in sync with the current view for deep-linking / sharing.
  useEffect(() => {
    writeDeepLink({
      frameworkId: selectedFrameworkId,
      domain: selectedDomain,
      control: selectedControl || undefined,
      ttp: selectedTTP || undefined,
    });
  }, [selectedFrameworkId, selectedDomain, selectedControl, selectedTTP]);

  // Relationship highlighting (build-brief §7.1): selecting an entity highlights
  // the entities it connects to across the other column and dims the rest. Sets
  // are null when there is no cross-column selection (no dimming applied).
  const highlightControlIds = useMemo(() => {
    if (!selectedTTP) return null;
    return new Set(
      getMitigatingControls(normaliseAttackId(selectedTTP)).map((m) => m.control.id),
    );
  }, [selectedTTP]);

  const highlightTtpAnchorIds = useMemo(() => {
    if (!selectedControl) return null;
    return new Set(getTechniquesCoveredBy(selectedControl).map((t) => t.technique.id));
  }, [selectedControl]);

  // Render helper for text highlighting
  const renderText = useMemo(() => {
    return (text) => highlightSearchText(text, searchQuery);
  }, [searchQuery]);

  // Defensive-coverage stats for the current pillar (drives the coverage meter).
  const coverageStats = useMemo(() => {
    const techs = getEntitiesByDomain(selectedDomain, 'offensive-technique');
    const total = techs.length;
    const covered = techs.filter((t) => getMitigatingControls(t.id).length > 0).length;
    return { total, covered, gaps: total - covered, pct: total ? Math.round((covered / total) * 100) : 0 };
  }, [selectedDomain]);

  const isAiDomain = selectedDomain === 'AI_ML';
  const threatTitle = isAiDomain ? 'AI Threats & Risks' : 'Adversary Techniques — Threat';
  const threatHint = isAiDomain
    ? 'MITRE ATLAS techniques and OWASP-LLM risks for the AI/ML pillar (being sourced).'
    : 'MITRE ATT&CK techniques relevant to this pillar.';

  const switchView = (mode) => {
    setShowComparison(false);
    setViewMode(mode);
  };

  return (
    <div className="tv-wrap">
      {/* brand chrome */}
      <div className="tv-chrome tv-fade" style={{ animationDelay: '.02s' }}>
        <div className="tv-brand">
          <div className="tv-mark">JL</div>
          <a className="tv-name" href="https://jasonlayton.com" target="_blank" rel="noopener noreferrer">
            jasonlayton.com
          </a>
        </div>
        <nav className="tv-nav">
          <button className={!showComparison && viewMode === 'explore' ? 'active' : ''} onClick={() => switchView('explore')}>
            Explorer
          </button>
          <button className={!showComparison && viewMode === 'coverage' ? 'active' : ''} onClick={() => switchView('coverage')}>
            Coverage
          </button>
        </nav>
      </div>

      {/* title block */}
      <div className="tv-head tv-fade" style={{ animationDelay: '.08s' }}>
        <div className="tv-eyebrow">Select Framework · Choose Domain ↔ Explore</div>
        <h1>Zero Trust Explorer</h1>
        <p className="tv-sub">
          Select a framework and a Zero Trust pillar, then click any technique or control to trace the
          relationships between them. Colours have meaning:{' '}
          <span style={{ color: 'var(--tv-defence)' }}>cyan is defence</span>,{' '}
          <span style={{ color: 'var(--tv-text)' }}>crimson red are attack techniques</span>,{' '}
          <span style={{ color: 'var(--tv-connection)' }}>coral is the connection</span>.
        </p>
      </div>

      {/* framework + domain bar */}
      <div className="tv-bar tv-fade" style={{ animationDelay: '.14s' }}>
        <FrameworkSelector
          frameworks={CONTROL_FRAMEWORKS}
          selectedFrameworkId={selectedFrameworkId}
          selectedLabel={selectedFramework}
          onFrameworkChange={handleFrameworkChange}
        />
        <DomainSelector selectedDomain={selectedDomain} onDomainChange={handleDomainChange} />
      </div>

      {/* search / command palette / export */}
      <div className="tv-actions tv-fade" style={{ animationDelay: '.18s' }}>
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <button className="tv-btn" onClick={() => setPaletteOpen(true)} title="Search all entities (Cmd/Ctrl-K)">
          <Command style={{ width: 14, height: 14 }} /> K
        </button>
        <button className="tv-btn" onClick={() => handleExport('md')} title="Export current view as Markdown (with provenance)">
          <Download style={{ width: 14, height: 14 }} /> Export
        </button>
        <button className="tv-btn" onClick={() => handleExport('json')} title="Export current view as JSON (with provenance)">
          JSON
        </button>
      </div>

      <SearchResults
        searchQuery={searchQuery}
        controlCount={totalControlCount}
        ttpCount={filteredTTPs.length}
      />

      {/* defensive coverage meter */}
      <div className="tv-coverage tv-fade" style={{ animationDelay: '.2s' }}>
        <span className="tv-lab">Defensive coverage</span>
        <div className="tv-meter">
          <div className="tv-fill" style={{ width: `${coverageStats.pct}%` }} />
        </div>
        <span className="tv-stat">
          {coverageStats.covered}/{coverageStats.total} techniques mitigated
          {coverageStats.gaps > 0 ? (
            <> · <span className="tv-gap">{coverageStats.gaps} gap{coverageStats.gaps > 1 ? 's' : ''}</span></>
          ) : (
            ' · full'
          )}
        </span>
      </div>

      {/* comparison overlay */}
      {showComparison && (
        <FrameworkComparison
          primaryControl={comparisonData.primaryControl}
          primaryFramework={comparisonData.primaryFramework}
          primaryDetails={comparisonData.primaryDetails}
          secondaryControl={comparisonData.secondaryControl}
          secondaryFramework={comparisonData.secondaryFramework}
          secondaryDetails={comparisonData.secondaryDetails}
          onClose={handleCloseComparison}
        />
      )}

      {/* coverage / gap analysis view */}
      {!showComparison && viewMode === 'coverage' && (
        <div className="tv-fade">
          <CoverageView
            domainId={selectedDomain}
            onSelectTechnique={(id) => {
              setViewMode('explore');
              handleTTPSelect(id);
            }}
          />
        </div>
      )}

      {/* explorer */}
      {!showComparison && viewMode === 'explore' && (
        <>
          <div className="tv-grid tv-fade" style={{ animationDelay: '.26s' }}>
            <ControlList
              controls={filteredControls}
              selectedControl={selectedControl}
              onControlSelect={handleControlSelect}
              renderText={renderText}
              controlName={(id) => getControlDetails(id)?.name || ''}
              hint={`${selectedFramework} · ${totalControlCount} controls in scope`}
              highlightIds={highlightControlIds}
            />
            <TTPList
              ttps={filteredTTPs}
              selectedTTP={selectedTTP}
              onTTPSelect={handleTTPSelect}
              renderText={renderText}
              title={threatTitle}
              hint={threatHint}
              highlightAnchorIds={highlightTtpAnchorIds}
            />
          </div>

          {selectedControl && (
            <ControlDetails
              controlId={selectedControl}
              details={controlDetails}
              renderText={renderText}
              onCompare={handleCompareControls}
              onClose={() => setSelectedControl('')}
              alternateFrameworkLabel={secondaryFramework}
            />
          )}

          {selectedTTP && (
            <TTPDetails
              ttpId={selectedTTP}
              details={ttpDetails}
              renderText={renderText}
              onClose={() => setSelectedTTP('')}
            />
          )}
        </>
      )}

      {/* legend + note */}
      <div className="tv-legend tv-fade">
        <span className="tv-li"><span className="tv-lk" style={{ background: 'var(--tv-defence)' }} />Defence · controls, D3FEND</span>
        <span className="tv-li"><span className="tv-lk" style={{ background: 'var(--tv-threat)' }} />Threat · ATT&amp;CK, ATLAS</span>
        <span className="tv-li"><span className="tv-lk" style={{ background: 'var(--tv-risk)' }} />Risk · OWASP-LLM, KEV</span>
        <span className="tv-li"><span className="tv-lk" style={{ background: 'var(--tv-connection)' }} />Connection · mappings</span>
      </div>
      <p className="tv-note">
        Mappings carry a <code>source</code> and <code>confidence</code> (authoritative / community /
        unverified) — sourced from cited datasets, never invented. CISA KEV is a point-in-time snapshot;
        ATLAS and OWASP-LLM are being developed.
      </p>

      {/* app-wide command palette */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onSelect={handlePaletteSelect}
      />
    </div>
  );
};

export default ZeroTrustExplorer;